import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { VagaItem } from './VagaItem';
import { formatDate, parseBackendDate } from '../../utils/formatters';
import { styles, colors } from '../../styles/globalStyles';

// Componente para o menu de horário (agrupando 4 vagas)
const HorarioMenu = ({
  horario,
  vagas,
  user,
  onAgendar,
  onDesmarcar,
  onSolicitarWaitlist,
  onSairDaWaitlist,
  isAdminView = false,
  onEditarVaga,
  onTrancarVaga,
  onDestrancarVaga,
  onLiberarVaga,
  naFilaMap = {},
  minhasFilas = [],
  userVagasNoDia = [],
  isMobile = false,
  createApi = null,
}) => {
  const [menuAberto, setMenuAberto] = useState(false);

  // Estatísticas do horário
  const stats = useMemo(() => {
    if (!vagas || vagas.length === 0) return {};
    
    return {
      total:     vagas.length,
      fixas:     vagas.filter(v => v.aluno_fixo_id).length,
      trancadas: vagas.filter(v => v.trancada && !v.aluno_fixo_id).length,
      livres:    vagas.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length,
      naFila:    vagas.filter(v => naFilaMap[v.id] !== undefined).length
    };
  }, [vagas, naFilaMap]);

  // Formatar hora
  const horaFormatada = vagas && vagas[0]?.horaFormatada || horario;

  return (
    <div style={{
      backgroundColor: colors.white,
      borderRadius: '12px',
      marginBottom: '12px',
      overflow: 'hidden',
      boxShadow: menuAberto 
        ? '0 4px 20px rgba(0,0,0,0.1)' 
        : '0 2px 8px rgba(0,0,0,0.05)',
      transition: 'all 0.3s ease',
      width: '100%'
    }}>
      {/* Cabeçalho do Menu de Horário */}
      <div 
        onClick={() => setMenuAberto(!menuAberto)}
        style={{
          padding: isMobile ? '14px' : '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          backgroundColor: menuAberto ? colors.primaryLight : colors.white,
          borderBottom: menuAberto ? `1px solid ${colors.grayLight}` : 'none'
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '10px' : '12px',
          flex: 1,
          minWidth: 0
        }}>
          <div style={{
            width: isMobile ? '45px' : '50px',
            height: isMobile ? '45px' : '50px',
            backgroundColor: menuAberto ? colors.primary : colors.grayLight,
            color: menuAberto ? colors.white : colors.dark,
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            transition: 'all 0.2s ease',
            flexShrink: 0
          }}>
            <div style={{ 
              fontSize: isMobile ? '15px' : '16px', 
              lineHeight: 1 
            }}>
              {horaFormatada.split(':')[0]}
            </div>
            <div style={{ 
              fontSize: isMobile ? '10px' : '11px', 
              opacity: 0.8,
              marginTop: '2px'
            }}>
              {horaFormatada.split(':')[1]}
            </div>
          </div>
          
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ 
              fontSize: isMobile ? '15px' : '16px', 
              fontWeight: '700', 
              color: colors.dark,
              marginBottom: '4px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {horaFormatada}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '13px', 
              color: colors.gray,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: colors.success,
                  borderRadius: '50%'
                }}></div>
                {stats.livres || 0} livres
              </span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: colors.warning,
                  borderRadius: '50%'
                }}></div>
                {stats.fixas || 0} fixas
              </span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: colors.danger,
                  borderRadius: '50%'
                }}></div>
                {stats.trancadas || 0} tranc.
              </span>
            </div>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flexShrink: 0
        }}>
          {stats.naFila > 0 && (
            <div style={{
              backgroundColor: colors.warningLight,
              color: colors.warningDark,
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '600',
              padding: '2px 6px',
              borderRadius: '10px',
              whiteSpace: 'nowrap'
            }}>
              {stats.naFila} na fila
            </div>
          )}
          
          <div style={{ 
            fontSize: '20px',
            color: colors.primary,
            transform: menuAberto ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0
          }}>
            ▼
          </div>
        </div>
      </div>
      
      {/* Conteúdo do Menu (Vagas) */}
      {menuAberto && (
        <div style={{ 
          padding: isMobile ? '12px' : '16px', 
          backgroundColor: colors.grayLight,
          borderTop: `1px solid ${colors.grayLight}`
        }}>
          {/* Container para vagas em layout vertical */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '10px' : '12px'
          }}>
            {vagas.map((vaga, index) => {
              const naFilaDireta = naFilaMap[vaga.id]?.naFila === true;
              const filaDoSlot = !naFilaDireta
                ? minhasFilas.find(f => f.data_hora === vaga.data_hora)
                : null;
              const naFila = naFilaDireta || filaDoSlot != null;
              const posicaoNaFila = naFilaMap[vaga.id]?.posicao || filaDoSlot?.posicao || 0;
              const sairDaFilaHandler = filaDoSlot
                ? (_id) => onSairDaWaitlist(filaDoSlot.vaga_id)
                : onSairDaWaitlist;

              return (
                <div
                  key={vaga.id || index}
                  style={{
                    animation: `fadeIn 0.3s ease ${index * 0.1}s both`,
                    width: '100%'
                  }}
                >
                  <VagaItem
                    vaga={vaga}
                    user={user}
                    onAgendar={onAgendar}
                    onDesmarcar={onDesmarcar}
                    onSolicitarWaitlist={onSolicitarWaitlist}
                    onSairDaWaitlist={sairDaFilaHandler}
                    isAdminView={isAdminView}
                    onEditarVaga={onEditarVaga}
                    onTrancarVaga={onTrancarVaga}
                    onDestrancarVaga={onDestrancarVaga}
                    onLiberarVaga={onLiberarVaga}
                    naFila={naFila}
                    posicaoNaFila={posicaoNaFila}
                    userVagasNoDia={userVagasNoDia}
                    isCompactView={true}
                    isMobile={isMobile}
                    createApi={isAdminView ? createApi : null}
                  />
                </div>
              );
            })}
          </div>
          
          {/* Resumo do Horário */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: colors.white,
            borderRadius: '8px',
            fontSize: isMobile ? '11px' : '12px',
            color: colors.gray
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>Total de vagas:</span>
              <span style={{ fontWeight: '600' }}>{stats.total}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>Livres:</span>
              <span style={{ fontWeight: '600', color: colors.success }}>
                {stats.livres}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Fixas:</span>
              <span style={{ fontWeight: '600', color: colors.warning }}>
                {stats.fixas}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Trancadas:</span>
              <span style={{ fontWeight: '600', color: colors.danger }}>
                {stats.trancadas}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente separado para o cabeçalho do dia
const DiaHeader = ({ dia, onDiaClick, isToday, isMobile }) => (
  <div 
    onClick={() => onDiaClick(dia.date)}
    style={{
      flex: '0 0 auto',
      width: isMobile ? '80px' : '85px',
      backgroundColor: dia.vagas.length > 0 ? colors.white : colors.grayLight,
      borderRadius: '12px',
      padding: isMobile ? '10px 6px' : '10px 8px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: dia.vagas.length > 0 
        ? '0 2px 6px rgba(0,0,0,0.08)' 
        : 'none',
      border: `2px solid ${
        isToday 
          ? colors.success 
          : dia.vagas.length > 0 
            ? colors.primaryLight 
            : 'transparent'
      }`,
      transform: isToday ? 'scale(1.05)' : 'scale(1)',
      position: 'relative',
      overflow: 'hidden'
    }}
  >
    {isToday && (
      <div style={{
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '6px',
        height: '6px',
        backgroundColor: colors.success,
        borderRadius: '50%'
      }} />
    )}
    
    <div style={{ 
      fontWeight: '700', 
      fontSize: isMobile ? '14px' : '15px', 
      color: isToday ? colors.success : colors.dark,
      marginBottom: '3px'
    }}>
      {dia.diaSemana}
    </div>
    
    <div style={{ 
      fontSize: isMobile ? '11px' : '12px', 
      color: isToday ? colors.success : colors.gray,
      marginBottom: '6px',
      fontWeight: isToday ? '600' : '400'
    }}>
      {dia.dataFormatada.split('/')[0]}/{dia.dataFormatada.split('/')[1]}
    </div>
    
    <div style={{ 
      padding: '3px 8px',
      backgroundColor: dia.vagas.length > 0 
        ? (isToday ? colors.success : colors.primary) 
        : colors.gray,
      color: colors.white,
      borderRadius: '12px',
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '600',
      display: 'inline-block',
      minWidth: '40px'
    }}>
      {dia.vagas.length} {dia.vagas.length === 1 ? 'vaga' : 'vagas'}
    </div>
  </div>
);

// Componente para o estado de loading
const LoadingState = ({ isMobile }) => (
  <div style={{
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: isMobile ? '30px 15px' : '40px 20px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    margin: isMobile ? '15px' : '20px'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '3px solid #e2e8f0',
      borderTopColor: colors.primary,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px'
    }}></div>
    <h3 style={{ 
      marginBottom: '10px', 
      color: colors.dark, 
      fontSize: isMobile ? '16px' : '18px' 
    }}>
      Carregando agenda...
    </h3>
    <p style={{ 
      color: colors.gray, 
      marginBottom: '0', 
      fontSize: isMobile ? '13px' : '14px' 
    }}>
      Buscando horários disponíveis
    </p>
  </div>
);

// Componente para o estado vazio
const EmptyState = ({ onRefresh, isMobile }) => (
  <div style={{
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: isMobile ? '30px 15px' : '40px 20px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    margin: isMobile ? '15px' : '20px'
  }}>
    <div style={{
      width: isMobile ? '60px' : '70px',
      height: isMobile ? '60px' : '70px',
      backgroundColor: colors.grayLight,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: isMobile ? '24px' : '28px'
    }}>
      📅
    </div>
    <h3 style={{ 
      marginBottom: '10px', 
      color: colors.dark, 
      fontSize: isMobile ? '16px' : '18px' 
    }}>
      Nenhuma vaga disponível
    </h3>
    <p style={{ 
      color: colors.gray, 
      marginBottom: '25px', 
      fontSize: isMobile ? '13px' : '14px',
      lineHeight: '1.5'
    }}>
      Não há vagas disponíveis para esta semana.
      Verifique se a semana foi gerada e publicada.
    </p>
    <button
      onClick={onRefresh}
      style={{
        ...styles.button,
        ...styles.buttonPrimary,
        padding: isMobile ? '10px 20px' : '12px 24px',
        fontSize: isMobile ? '14px' : '15px',
        fontWeight: '600',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
      }}
    >
      ↻ Atualizar Agenda
    </button>
  </div>
);

export const GradeSemanalMobile = ({
  grade,
  user,
  onAgendar,
  onDesmarcar,
  onSolicitarWaitlist,
  onSairDaWaitlist,
  isAdminView = false,
  onEditarVaga,
  onTrancarVaga,
  onDestrancarVaga,
  onLiberarVaga,
  naFilaMap = {},
  minhasFilas = [],
  userVagasNoDia = [],
  onRefresh,
  visualizacaoConfig = null,
  createApi = null,
}) => {
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [horariosAbertos, setHorariosAbertos] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const hoje = new Date();
  const hojeStr = hoje.toISOString().split('T')[0];

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // FUNÇÃO PARA FILTRAR VAGAS PASSADAS
  const filtrarVagasPassadas = useCallback((vagas) => {
    if (!vagas || vagas.length === 0) return vagas;

    const janelaDia = visualizacaoConfig?.janela_dia ?? 1;
    const exibirOcupadas = visualizacaoConfig?.exibir_vagas_ocupadas ?? true;

    if (janelaDia >= 0) {
      // Backend já filtrou pelo janela_dia — frontend só aplica a regra de vagas ocupadas
      if (exibirOcupadas) return vagas;
      return vagas.filter(v => !v.agendamento_id || v.aluno_google_id === user?.googleId);
    }

    // janelaDia < 0: bloquear vagas que começam nas próximas |janelaDia|h
    const horasAntecedencia = Math.abs(janelaDia);
    const tempoLimite = new Date(Date.now() + horasAntecedencia * 60 * 60 * 1000);

    return vagas.filter(vaga => {
      const dataVaga = parseBackendDate(vaga.data_hora);
      if (!dataVaga) return false;
      if (!exibirOcupadas && vaga.agendamento_id && vaga.aluno_google_id !== user?.googleId) return false;
      return dataVaga >= tempoLimite;
    });
  }, [visualizacaoConfig, user?.googleId]);

  // Memoizar o processamento dos dias com filtro aplicado
  const { dias, diaAtualIndex, stats } = useMemo(() => {
    // Se for admin view, não filtrar vagas passadas
    const gradeFiltrada = isAdminView ? grade : filtrarVagasPassadas(grade);
    
    if (!gradeFiltrada || gradeFiltrada.length === 0) {
      return { dias: [], diaAtualIndex: -1, stats: {} };
    }
    
    // Determinar a data de referência
    let dataReferencia;
    if (gradeFiltrada.length > 0 && gradeFiltrada[0].data_hora) {
      dataReferencia = parseBackendDate(gradeFiltrada[0].data_hora);
    } else {
      dataReferencia = new Date();
    }
    
    // Ajustar para início da semana (segunda-feira)
    const diaSemana = dataReferencia.getDay();
    const diff = diaSemana === 0 ? -6 : 1 - diaSemana;
    
    const inicioSemana = new Date(dataReferencia);
    inicioSemana.setDate(dataReferencia.getDate() + diff);
    inicioSemana.setHours(0, 0, 0, 0);
    
    // Criar 7 dias a partir da segunda
    const diasArray = Array.from({ length: 7 }, (_, i) => {
      const data = new Date(inicioSemana);
      data.setDate(inicioSemana.getDate() + i);
      const diaStr = data.toISOString().split('T')[0];
      return {
        date: data,
        diaSemana: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'][i],
        dataFormatada: formatDate(data),
        diaStr,
        vagas: [],
        vagasAgrupadas: {}, // Novo: agrupar vagas por horário
        horarios: [], // Lista de horários do dia
        isToday: diaStr === hojeStr
      };
    });
    
    // Encontrar índice do dia atual
    const diaAtualIdx = diasArray.findIndex(dia => dia.isToday);
    
    // Organizar vagas por dia
    const vagasPorDia = new Map();
    diasArray.forEach(dia => vagasPorDia.set(dia.diaStr, []));
    
    gradeFiltrada.forEach(vaga => {
      const dataVaga = parseBackendDate(vaga.data_hora);
      if (!dataVaga) return;
      
      const dataVagaStr = dataVaga.toISOString().split('T')[0];
      const diaVagas = vagasPorDia.get(dataVagaStr);
      
      if (diaVagas) {
        diaVagas.push({ 
          ...vaga, 
          hora: dataVaga.getHours(),
          minuto: dataVaga.getMinutes(),
          horaFormatada: dataVaga.toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          timestamp: dataVaga.getTime(),
          horarioKey: `${dataVaga.getHours().toString().padStart(2, '0')}:${dataVaga.getMinutes().toString().padStart(2, '0')}`
        });
      }
    });
    
    // Processar cada dia: agrupar vagas por horário
    diasArray.forEach(dia => {
      const vagasDoDia = vagasPorDia.get(dia.diaStr) || [];
      dia.vagas = vagasDoDia;
      
      // Ordenar por horário
      vagasDoDia.sort((a, b) => a.timestamp - b.timestamp);
      
      // Agrupar por horário
      const agrupadas = {};
      const horariosSet = new Set();
      
      vagasDoDia.forEach(vaga => {
        const horarioKey = vaga.horarioKey;
        horariosSet.add(horarioKey);
        
        if (!agrupadas[horarioKey]) {
          agrupadas[horarioKey] = [];
        }
        agrupadas[horarioKey].push(vaga);
      });
      
      dia.vagasAgrupadas = agrupadas;
      dia.horarios = Array.from(horariosSet).sort();
    });
    
    // Calcular estatísticas
    const agora = new Date();
    const stats = {
      totalVagas:     gradeFiltrada.length,
      vagasFixas:     gradeFiltrada.filter(v => v.aluno_fixo_id).length,
      vagasTrancadas: gradeFiltrada.filter(v => v.trancada && !v.aluno_fixo_id).length,
      vagasOcupadas:  gradeFiltrada.filter(v => v.agendamento_id && !v.trancada).length,
      vagasLivres:    gradeFiltrada.filter(v => !v.aluno_fixo_id && !v.trancada && !v.agendamento_id && !v.rascunho).length,
      diasComVagas: diasArray.filter(d => d.vagas.length > 0).length,
      totalHorarios: diasArray.reduce((acc, dia) => acc + dia.horarios.length, 0)
    };
    
    const diasFiltrados = isAdminView ? diasArray : diasArray.filter(d => d.vagas.length > 0);
    // Recalcula índice do dia atual no array já filtrado
    const diaAtualIdxFiltrado = diasFiltrados.findIndex(dia => dia.isToday);

    return {
      dias: diasFiltrados,
      diaAtualIndex: diaAtualIdxFiltrado,
      stats
    };
  }, [grade, hojeStr, filtrarVagasPassadas, isAdminView]);

  // Auto-selecionar o dia atual ou primeiro dia com vagas
  useEffect(() => {
    if (dias.length > 0) {
      // Se tiver dia atual com vagas, seleciona ele
      if (diaAtualIndex !== -1 && dias[diaAtualIndex].vagas.length > 0) {
        setDiaSelecionado(dias[diaAtualIndex].date);
      } else {
        // Seleciona o primeiro dia com vagas
        const primeiroDiaComVagas = dias.find(d => d.vagas.length > 0);
        if (primeiroDiaComVagas) {
          setDiaSelecionado(primeiroDiaComVagas.date);
        } else if (dias.length > 0) {
          setDiaSelecionado(dias[0].date);
        }
      }
    }
  }, [dias, diaAtualIndex]);

  // Filtrar dias para mostrar (apenas dias com vagas)
  const diasComVagas = useMemo(() => 
    dias.filter(dia => dia.vagas.length > 0),
    [dias]
  );

  // Dia atualmente selecionado (com vagas)
  const diaAtual = useMemo(() => 
    dias.find(dia => dia.date.getTime() === (diaSelecionado?.getTime() || 0)) ||
    diasComVagas[0] ||
    null,
    [dias, diaSelecionado, diasComVagas]
  );

  // Scroll para o dia selecionado
  const scrollToDia = useCallback((date) => {
    const elementId = `dia-${date.getTime()}`;
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    setDiaSelecionado(date);
  }, []);

  // Controle de scroll horizontal
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollLeft = container.scrollLeft;
    setScrollPosition(scrollLeft);
    
    // Auto-selecionar dia baseado na posição do scroll
    const dayWidth = 80 + 8; // width + gap (ajustado para mobile)
    const dayIndex = Math.round(scrollLeft / dayWidth);
    if (dias[dayIndex] && dias[dayIndex].vagas.length > 0) {
      setDiaSelecionado(dias[dayIndex].date);
    }
  }, [dias]);

  // Refresh controlado
  const handleRefresh = useCallback(() => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  }, [onRefresh]);

  // Abrir/fechar todos os horários de uma vez
  const toggleTodosHorarios = useCallback(() => {
    if (!diaAtual) return;
    
    const todosHorarios = diaAtual.horarios;
    const todosAbertos = todosHorarios.every(horario => horariosAbertos[horario]);
    
    if (todosAbertos) {
      // Fechar todos
      setHorariosAbertos({});
    } else {
      // Abrir todos
      const novosAbertos = {};
      todosHorarios.forEach(horario => {
        novosAbertos[horario] = true;
      });
      setHorariosAbertos(novosAbertos);
    }
  }, [diaAtual, horariosAbertos]);

  // Loading state
  if (!grade) {
    return <LoadingState isMobile={isMobile} />;
  }

  // Empty state
  if (grade.length === 0 || diasComVagas.length === 0) {
    return <EmptyState onRefresh={handleRefresh} isMobile={isMobile} />;
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      paddingBottom: '80px',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Header Principal */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, #0f5a30 100%)`,
        color: colors.white,
        padding: isMobile ? '16px 12px' : '20px 16px',
        borderBottomLeftRadius: '20px',
        borderBottomRightRadius: '20px',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '10px' : '12px', 
          marginBottom: isMobile ? '10px' : '12px' 
        }}>
          <div style={{
            width: isMobile ? '40px' : '44px',
            height: isMobile ? '40px' : '44px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? '18px' : '20px',
            backdropFilter: 'blur(10px)'
          }}>
            🤸‍♀
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '20px', 
              fontWeight: '700',
              marginBottom: isMobile ? '4px' : '4px'
            }}>
              Agenda Semanal
            </h1>
            <p style={{ 
              margin: 0, 
              opacity: 0.9, 
              fontSize: isMobile ? '12px' : '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>📅</span>
              {dias.length > 0 && `${formatDate(dias[0].date)} - ${formatDate(dias[dias.length - 1].date)}`}
            </p>
          </div>
        </div>
        
        {/* Stats em linha */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: isMobile ? '12px' : '16px',
          paddingTop: isMobile ? '10px' : '12px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          fontSize: isMobile ? '11px' : '12px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: isMobile ? '13px' : '14px', 
              fontWeight: '700' 
            }}>
              {stats.totalVagas}
            </div>
            <div style={{ opacity: 0.9 }}>Total</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#fef3c7' }}>
              {stats.vagasFixas}
            </div>
            <div style={{ opacity: 0.9 }}>Fixas</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#fca5a5' }}>
              {stats.vagasTrancadas}
            </div>
            <div style={{ opacity: 0.9 }}>Trancadas</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#fda4af' }}>
              {stats.vagasOcupadas}
            </div>
            <div style={{ opacity: 0.9 }}>Ocupadas</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#d1fae5' }}>
              {stats.vagasLivres}
            </div>
            <div style={{ opacity: 0.9 }}>Livres</div>
          </div>
        </div>
      </div>

      {/* Navegação Horizontal de Dias */}
      <div style={{ 
        padding: isMobile ? '12px' : '16px',
        backgroundColor: colors.white,
        marginTop: '-10px',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        position: 'relative'
      }}>
        <div style={{
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          color: colors.dark,
          marginBottom: isMobile ? '10px' : '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Selecione o dia:</span>
          <span style={{
            fontSize: isMobile ? '11px' : '12px',
            color: colors.gray,
            fontWeight: 'normal'
          }}>
            {diasComVagas.length} de 7 dias
          </span>
        </div>
        
        <div style={{
          position: 'relative',
          padding: '2px 0'
        }}>
          {/* Indicador de scroll */}
          {scrollPosition > 10 && (
            <div style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              zIndex: 10
            }}>
              ←
            </div>
          )}
          
          <div 
            style={{ 
              display: 'flex', 
              overflowX: 'auto',
              gap: '8px',
              padding: '4px 0',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
            onScroll={handleScroll}
          >
            {dias.map((dia, index) => (
              <DiaHeader
                key={index}
                dia={dia}
                onDiaClick={scrollToDia}
                isToday={dia.isToday}
                isMobile={isMobile}
              />
            ))}
          </div>
          
          <div style={{
            position: 'absolute',
            right: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px'
          }}>
            →
          </div>
        </div>
        
        {/* Indicador de dia selecionado */}
        {diaAtual && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: isMobile ? '10px' : '12px',
            padding: isMobile ? '6px 10px' : '8px 12px',
            backgroundColor: colors.grayLight,
            borderRadius: '8px',
            fontSize: isMobile ? '12px' : '13px'
          }}>
            <span style={{ 
              color: colors.primary, 
              fontWeight: '600',
              marginRight: '6px'
            }}>
              {diaAtual.diaSemana}, {diaAtual.dataFormatada}
            </span>
            <span style={{ color: colors.gray }}>
              • {diaAtual.vagas.length} vagas
            </span>
          </div>
        )}
      </div>

      {/* Lista de Vagas do Dia Selecionado - AGORA COM MENUS DE HORÁRIO */}
      <div style={{ 
        padding: isMobile ? '12px' : '16px',
        minHeight: '300px'
      }}>
        {diaAtual ? (
          <div id={`dia-${diaAtual.date.getTime()}`}>
            {/* Cabeçalho do Dia com controle de horários */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: isMobile ? '12px' : '16px',
              paddingBottom: isMobile ? '10px' : '12px',
              borderBottom: `2px solid ${colors.grayLight}`
            }}>
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: colors.dark
                  }}>
                    {diaAtual.diaSemana}
                  </div>
                  {diaAtual.isToday && (
                    <div style={{
                      backgroundColor: colors.success,
                      color: colors.white,
                      fontSize: '10px',
                      fontWeight: '700',
                      padding: '2px 6px',
                      borderRadius: '10px'
                    }}>
                      HOJE
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '13px' : '14px', 
                  color: colors.gray,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>📅</span>
                  <span>{diaAtual.dataFormatada}</span>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <button
                  onClick={toggleTodosHorarios}
                  style={{
                    ...styles.button,
                    ...styles.buttonOutline,
                    ...styles.buttonSmall,
                    padding: isMobile ? '6px 10px' : '6px 12px',
                    fontSize: isMobile ? '11px' : '12px',
                    borderRadius: '8px'
                  }}
                >
                  {diaAtual.horarios.every(horario => horariosAbertos[horario])
                    ? 'Fechar Todos'
                    : 'Abrir Todos'}
                </button>
                
                <div style={{
                  ...styles.badge,
                  ...styles.badgePrimary,
                  fontSize: isMobile ? '12px' : '13px',
                  padding: isMobile ? '6px 10px' : '6px 12px'
                }}>
                  {diaAtual.horarios.length} horários
                </div>
              </div>
            </div>

            {/* Lista de Menus por Horário */}
            {diaAtual.horarios.length > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: isMobile ? '10px' : '12px' 
              }}>
                {diaAtual.horarios.map((horario, idx) => {
                  const vagasDoHorario = diaAtual.vagasAgrupadas[horario] || [];
                  
                  return (
                    <div 
                      key={horario} 
                      style={{ 
                        animation: `fadeIn 0.3s ease ${idx * 0.05}s both`
                      }}
                    >
                      <HorarioMenu
                        horario={horario}
                        vagas={vagasDoHorario}
                        user={user}
                        onAgendar={onAgendar}
                        onDesmarcar={onDesmarcar}
                        onSolicitarWaitlist={onSolicitarWaitlist}
                        onSairDaWaitlist={onSairDaWaitlist}
                        isAdminView={isAdminView}
                        onEditarVaga={onEditarVaga}
                        onTrancarVaga={onTrancarVaga}
                        onDestrancarVaga={onDestrancarVaga}
                        onLiberarVaga={onLiberarVaga}
                        naFilaMap={naFilaMap}
                        minhasFilas={minhasFilas}
                        userVagasNoDia={userVagasNoDia}
                        isMobile={isMobile}
                        createApi={createApi}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: isMobile ? '30px 15px' : '40px 20px',
                backgroundColor: colors.white,
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  fontSize: isMobile ? '32px' : '40px',
                  marginBottom: isMobile ? '10px' : '12px',
                  opacity: 0.5
                }}>
                  🕒
                </div>
                <h4 style={{ 
                  marginBottom: '8px', 
                  color: colors.dark,
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  Nenhum horário neste dia
                </h4>
                <p style={{ 
                  color: colors.gray, 
                  fontSize: isMobile ? '13px' : '14px',
                  marginBottom: '0'
                }}>
                  Não há vagas disponíveis para {diaAtual.diaSemana}.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '30px 15px' : '40px 20px'
          }}>
            <div style={{
              fontSize: isMobile ? '32px' : '40px',
              marginBottom: isMobile ? '10px' : '12px',
              opacity: 0.5
            }}>
              📅
            </div>
            <h4 style={{ 
              marginBottom: '8px', 
              color: colors.dark,
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Selecione um dia
            </h4>
            <p style={{ 
              color: colors.gray, 
              fontSize: isMobile ? '13px' : '14px',
              marginBottom: '20px'
            }}>
              Escolha um dia na barra superior para ver os horários.
            </p>
          </div>
        )}
      </div>

      {/* Footer Fixo */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: colors.white,
        borderTop: `1px solid ${colors.grayLight}`,
        padding: isMobile ? '10px 12px' : '12px 16px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '13px', 
            color: colors.gray,
            marginBottom: '4px'
          }}>
            {diasComVagas.length} dias • {stats.totalHorarios || 0} horários
          </div>
          <div style={{ 
            fontSize: isMobile ? '10px' : '11px', 
            color: colors.gray,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '8px'
          }}>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              gap: '4px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#d1fae5',
                borderRadius: '2px'
              }}></div>
              Disponível
            </span>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              gap: '4px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#fef3c7',
                borderRadius: '2px'
              }}></div>
              Ocupado
            </span>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleRefresh}
            style={{
              ...styles.button,
              ...styles.buttonOutline,
              ...styles.buttonSmall,
              padding: isMobile ? '6px 10px' : '8px 12px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Atualizar"
          >
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>↻</span>
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
